// App.tsx
import React from 'react';
import { ArrowLeft, Github, ExternalLink, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import av from "@/assets/achivements/av.jpg";
import avgit from "@/assets/achivements/avgit.png";
import Project5 from "@/assets/achivements/Project5.png";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const Avinyaa_2025: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Avinyaa 2025 Website Development</h1>
            
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">React</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Next.js</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Tailwind CSS</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Vercel</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">GitHub Actions</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
              <img src={av} />

              </div>
              <div>
                <img src={Project5} alt="Avinyaa 2025 Website" className="w-full object-cover" />
              </div>
              <div>
                <img src={avgit} alt="Placeholder" className="w-full object-cover" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Led the website development team for Avinyaa 2025, the flagship symposium of R.M.D. Engineering 
                  College AIML Department. Engineered a fully customized website optimizing performance, usability, 
                  and accessibility, with a focus on seamless integration and deployment.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Development Approach</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The project followed a collaborative development workflow using modern frontend technologies. 
                  We implemented a component-based architecture with React and Next.js to create a fast, responsive 
                  website optimized for all devices. The team utilized Tailwind CSS for efficient styling and 
                  established a CI/CD pipeline with GitHub Actions for automated testing and deployment to Vercel.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li>Responsive design optimized for all device sizes</li>
                  <li>Dynamic event registration and management system</li>
                  <li>Integrated payment processing for event tickets</li>
                  <li>Real-time schedule updates and notifications</li>
                  <li>Interactive venue maps and navigation</li>
                  <li>Team and speaker profiles with social media integration</li>
                  <li>SEO optimization for maximum visibility</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The website was built using Next.js for server-side rendering capabilities, which significantly 
                  improved initial load times and SEO performance. We implemented a JAMstack architecture, combining 
                  client-side JavaScript, reusable APIs, and pre-built Markup. The site features lazy loading for 
                  images and components, route-based code splitting, and optimized asset delivery. For styling, 
                  we used Tailwind CSS with custom design tokens to maintain consistency across the site while 
                  ensuring a unique visual identity for the symposium.
                </p>
              </div>
              
              {/* Live site demo */}
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Live Website</h2>
                <div className="aspect-video w-full overflow-hidden rounded-lg">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://avinyaa2025.web.app" 
                    title="Avinyaa 2025 Website" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    className="aspect-video"
                  ></iframe>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/symposium_2025" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://avinyaa2025.web.app" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Globe className="mr-2 h-5 w-5" />
                      <span>Live Website</span>
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">R</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">React</p>
                      <p className="text-sm text-muted-foreground">Frontend framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">N</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Next.js</p>
                      <p className="text-sm text-muted-foreground">React framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">T</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Tailwind CSS</p>
                      <p className="text-sm text-muted-foreground">Utility-first CSS</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">V</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Vercel</p>
                      <p className="text-sm text-muted-foreground">Deployment platform</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Outcome</h3>
                <p className="text-muted-foreground mb-4">
                  The Avinyaa 2025 website significantly enhanced the symposium's digital presence, contributing 
                  to increased registration numbers and improved participant experience.
                </p>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Performance Score</span>
                  <span className="text-primary font-medium">95/100</span>
                </div>
                <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full w-[95%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Inventory_Management_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Inventory Management System</h3>
              <p className="text-muted-foreground line-clamp-2">A system designed to streamline stock tracking and optimize warehouse operations.</p>
            </Link>
            
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A self-hosted cloud storage solution using TrueNAS on a repurposed machine.</p>
            </Link>
            
            <Link to="/Dual_Axis_Solar_Tracker" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Dual Axis Solar Tracker</h3>
              <p className="text-muted-foreground line-clamp-2">A system that optimizes solar energy collection by adjusting solar panels in two axes.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Avinyaa_2025;
